import { jsx } from "panel";
import {
  FiltersOperator,
  RetentionCountType,
  RowLabelType
} from "common/query-builder/query-entry-section/types";
import { QueryBlockEl } from "common/query-builder/query-block";
import { ButtonSize } from "common/components/button/constants";
import { SelectTheme } from "common/components/select/constants";
import { ChartType, CountUsersOnceState, ReportName, ResourceType, RETENTION_MATH_TYPES } from "common/report/constants";
import { renameEvent } from "common/query-builder/cohorts-query-builder/util";
import { QueryEntrySectionEl } from "common/query-builder/query-entry-section";
import { BaseEntryRowEl } from "common/query-builder/query-entry-section/row-types/base/template";
import { dataGroupStore } from "common/report/stores/data-groups";
import { Placement } from "common/components/tooltip/types";
import { RetentionCriteriaEl } from "common/report/retention/query-builder/retention-criteria";
import { RetentionQuerySettingsEl } from "common/report/retention/query-builder/retention-query-settings";
import { MetricType } from "common/query-builder/query-block/constants";
import { SelectEl } from "common/components/select";
import { Directions } from "common/components/drop-menu/types";
import { MultiAttributionMenuEl } from "common/widgets/multi-attribution-menu";
import { MultiAttributionType } from "common/widgets/multi-attribution-menu/types";
import { DefaultMultiAttribution } from "common/widgets/multi-attribution-menu/constants";
import { getRetentionMetricAttributionItems } from "common/report/insights/util/retention-attribution";
export function RetentionMetricEntryRowEl({ section, entry, index }) {
  const behaviorRenderer = () => /* @__PURE__ */ jsx("div", {
    sel: ".mp-metric-entry-behavior"
  }, /* @__PURE__ */ jsx(QueryEntrySectionEl, {
    key: "retentionEntries",
    attrs: {
      ...section.helpers.getNestedSectionAttrs(),
      elref: `entrySection`,
      "allow-cohort-filter": false,
      autofocus: true,
      "autoactivate-placeholders": false,
      "empty-placeholder": {
        entryIndexLabelToggle: entry.behavior.entryIndexLabelToggle,
        event: {
          custom: false,
          id: null,
          label: `Select Event`,
          renamed: null,
          value: null
        },
        filters: [],
        filtersOperator: FiltersOperator.And,
        type: ResourceType.Event
      },
      "disable-add-entry": true,
      "min-entries": entry.behavior.type === MetricType.Retention ? 2 : 1,
      "row-draggable": true,
      "row-limit": entry.behavior.type === MetricType.Retention ? 2 : 1,
      "row-types": [ResourceType.Event],
      "row-label-type": RowLabelType.Numeric,
      "report-name": ReportName.Retention,
      "show-all-events": false,
      "show-any-event": true,
      "show-top-events": false,
      "show-sessions": false,
      "other-filter-options": [],
      entries: entry.behavior.behaviors.map((behavior) => ({
        entryIndexLabelToggle: entry.behavior.entryIndexLabelToggle,
        event: {
          custom: behavior.type === MetricType.CustomEvent,
          id: behavior.id,
          label: renameEvent(behavior.name) || `Select Event`,
          renamed: behavior.renamed,
          value: behavior.name
        },
        filters: behavior.filters,
        filtersDeterminer: behavior.filtersDeterminer,
        type: ResourceType.Event
      }))
    },
    on: {
      update: this.handleEntryUpdate
    }
  }), /* @__PURE__ */ jsx(null, null, entry.behavior.type === MetricType.Retention ? /* @__PURE__ */ jsx(RetentionCriteriaEl, {
    attrs: {
      unbounded: entry.behavior.retentionUnbounded,
      "retention-unit": entry.behavior.retentionUnit,
      "bucket-sizes": entry.behavior.retentionCustomBucketSizes,
      "retention-alignment-type": entry.behavior.retentionAlignmentType,
      "data-group-id": entry.behavior.dataGroupId
    },
    on: {
      changeSetting: this.handleSettingsChanged,
      changeUnit: this.handleRetentionUnitChanged,
      applyBucketSizes: this.handleBucketSizesApplied,
      "toggle-retention-alignment": this.handleToggleRetentionAlignment
    }
  }) : /* @__PURE__ */ jsx(RetentionQuerySettingsEl, {
    attrs: {
      "retention-type": entry.behavior.retentionType,
      "counting-method": this.getCountingMethod()
    },
    on: { change: this.handleSettingsChanged }
  })));
  const measurementRenderer = () => {
    var _a;
    return /* @__PURE__ */ jsx("div", {
      sel: ".mp-metric-entry-measurement"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".mp-metric-entry-measurement-inner"
    }, /* @__PURE__ */ jsx(QueryBlockEl, {
      sel: ".count-menu",
      attrs: {
        elref: `countBlock`,
        type: `count`,
        "metric-type": entry.behavior.type,
        "data-group-id": entry.behavior.dataGroupId || ``,
        "data-groups": Object.values(dataGroupStore.value),
        "project-id": section.attr(`project-id`),
        "selector-type": `button`,
        "count-users-once-state": section.getMultiAttribution(index) ? CountUsersOnceState.Required : section.attr(`count-users-once-state`),
        selected: {
          countType: entry.measurement.aggregationOperator === RETENTION_MATH_TYPES.RETENTION_RATE ? RetentionCountType.RetentionRate : RetentionCountType.Unique,
          countUsersOnceType: entry.measurement.countUsersOnceType
        },
        "highlight-selections": true,
        theme: SelectTheme.QueryBuilder,
        "button-size": ButtonSize.Small,
        "track-name": `retention`,
        "user-id": section.attr(`user-id`),
        "workspace-id": section.attr(`workspace-id`)
      },
      on: {
        select: this.handleRetentionCountBySelected
      }
    }), section.attr(`chart-type`) === ChartType.RetentionCurve && /* @__PURE__ */ jsx(SelectEl, {
      attrs: {
        "always-show-caret": true,
        theme: SelectTheme.Tertiary,
        size: ButtonSize.Small,
        "menu-alignment": Directions.Right,
        "menu-width": 184,
        "disable-first-item-padding": true,
        elref: `retentionBucketIndexSelect`
      },
      props: {
        sections: [
          {
            label: `Retention groups`,
            icon: `info-xs`,
            tooltip: {
              "max-width": `120px`,
              placement: Placement.Right,
              text: `View how specific Retention Groups trend over time. [Learn more](https://docs.mixpanel.com/docs/analysis/reports/retention#retention-trends)`
            },
            items: [
              {
                label: `All Groups`,
                isSelected: true,
                value: null
              }
            ]
          }
        ]
      }
    }), [ChartType.Line, ChartType.Metric].includes(section.attr(`chart-type`)) && /* @__PURE__ */ jsx(SelectEl, {
      attrs: {
        "always-show-caret": true,
        theme: SelectTheme.Tertiary,
        size: ButtonSize.Small,
        "menu-alignment": Directions.Right,
        "menu-width": 184,
        "disable-first-item-padding": true,
        elref: `retentionBucketIndexSelect`
      },
      props: {
        sections: [
          {
            label: `Retention groups`,
            icon: `info-xs`,
            tooltip: {
              "max-width": `120px`,
              placement: Placement.Right,
              text: `View how specific Retention Groups trend over time. [Learn more](https://docs.mixpanel.com/docs/analysis/reports/retention#retention-trends)`
            },
            items: entry.measurement.retentionBuckets
          }
        ]
      },
      on: {
        change: this.handleRetentionBucketIndexSelected
      }
    })), !section.attr(`can-use-multi-attribution`) && section.attr(`can-use-breakdown-attribution`) && /* @__PURE__ */ jsx("div", {
      sel: ".mp-metric-entry-measurement-inner"
    }, /* @__PURE__ */ jsx(SelectEl, {
      attrs: {
        elref: `attributionSelect`,
        "always-show-caret": true,
        sections: [{ items: getRetentionMetricAttributionItems(entry) }],
        label: `${section.attr(`breakdown-attribution-label-prefix`)} on ${((_a = getRetentionMetricAttributionItems(entry).find((entry2) => entry2.isSelected)) == null ? void 0 : _a.label) || `...`}`,
        "selected-option": ``,
        size: ButtonSize.Small,
        theme: SelectTheme.Tertiary,
        "menu-width": 0,
        "tab-index": 0,
        "experimental-positioning": true
      },
      on: { change: this.handleAttributionSelection }
    })), section.attr(`can-use-multi-attribution`) && /* @__PURE__ */ jsx("div", {
      sel: ".mp-metric-entry-measurement-inner"
    }, /* @__PURE__ */ jsx(SelectEl, {
      sel: ".subitem-select.multi-attribution-type",
      attrs: {
        elRef: `multiAttributionSelector`,
        "always-show-caret": true,
        label: section.getMultiAttributionLabel(index),
        title: section.isMultiAttributionMenuOpen(index) ? `` : section.getMultiAttributionTitle(index),
        open: section.isMultiAttributionMenuOpen(index),
        theme: SelectTheme.Tertiary,
        size: ButtonSize.Small
      },
      on: {
        menuChange: (ev) => section.onMultiAttributionMenuChange(ev, index)
      }
    }, /* @__PURE__ */ jsx("div", {
      attrs: { slot: `body` }
    }, section.isMultiAttributionMenuOpen(index) && /* @__PURE__ */ jsx(MultiAttributionMenuEl, {
      params: {
        handleCancel: () => {
          section.onMultiAttributionCancel();
        },
        handleChange: (ev) => {
          section.onMultiAttributionChange(ev, index);
        },
        handleDelete: () => {
          section.onMultiAttributionDelete(index);
        },
        isSupportedMultiAttributionType: (type) => section.isSupportedMultiAttributionTypeForEntry(type, index),
        showMultiAttributionType: (type) => [
          MultiAttributionType.LastTouch,
          MultiAttributionType.FirstTouch,
          MultiAttributionType.Participation
        ].includes(type),
        metricIndex: index,
        multiAttribution: section.getMultiAttribution(index) || DefaultMultiAttribution,
        projectIsFreePlan: section.attr(`project-is-free-plan`)
      }
    })))));
  };
  return /* @__PURE__ */ jsx(BaseEntryRowEl, {
    el: section,
    entry,
    index,
    showLabels: false,
    hideActions: true,
    rowRenderer: {
      selectorRenderer: behaviorRenderer,
      bodyRowRenderer: measurementRenderer
    },
    isPlaceholder: this.isPlaceholder()
  });
}
